import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import { Alert, Box, Chip, CircularProgress, Grid, Paper, Tab, Table, TableBody, TableCell, TableHead, TableRow, Tabs, Typography } from '@mui/material';
import { Bar, CartesianGrid, ComposedChart, Legend, Line, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import React, { useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useStore } from 'src/stores';
import { FAILURE_CLASS_LABELS, STALE_AFTER_HOURS, ingestSummary, isProviderHealthy, isStale, sortProviders, toDateSeries } from './health';
import { CoverageByGrain } from './CoverageByGrain';
import { FleetSummary } from './FleetSummary';
import { Funnel } from './Funnel';
import { ProviderCoverage } from './ProviderCoverage';
import { useAsyncList } from './useAsyncList';
import { WipBanner } from 'src/pages/AdminPage/WipBanner';
const formatWhen = value => value ? new Date(value).toLocaleString() : 'unknown';

/** Bill Scraping: staff-only scraper health. Every number comes from the sweep's published summary. */
/** The sweep's own numbers: what ran, what landed, and what reached the app. */
const ScraperOverview = observer(() => {
  var _latest$totals$ok, _latest$totals, _latest$totals$provid, _latest$totals2, _latest$totals$pdfs, _latest$totals3, _latest$totals$s3_upl, _latest$totals4;
  const store = useStore();
  const state = useAsyncList(store.admin.getBillScrapingHealthAsync, () => store.admin.getBillScrapingHealthAsync.run(30), 'Could not load scraper health.', [store]);
  if (state.loading) {
    return /*#__PURE__*/React.createElement(Box, {
      sx: {
        padding: '32px',
        textAlign: 'center'
      }
    }, /*#__PURE__*/React.createElement(CircularProgress, null));
  }
  if (state.error) {
    return /*#__PURE__*/React.createElement(Alert, {
      severity: "error",
      sx: {
        marginTop: '16px'
      }
    }, state.error);
  }
  if (!state.rows.length) {
    // Distinct from staleness: nothing published yet is a different problem from published-but-frozen.
    return /*#__PURE__*/React.createElement(Alert, {
      severity: "info",
      sx: {
        marginTop: '16px'
      }
    }, "The scraper has not published a run summary yet. It publishes one at the end of each sweep.");
  }
  const latest = state.rows[0];
  const providers = sortProviders(latest.providers);
  const series = toDateSeries(state.rows);
  const ingest = ingestSummary(latest);
  const actionable = providers.filter(p => p.action_required);
  return /*#__PURE__*/React.createElement(Box, {
    sx: {
      marginTop: '16px'
    }
  }, isStale(state.rows) ? /*#__PURE__*/React.createElement(Alert, {
    severity: "warning",
    sx: {
      marginBottom: '16px'
    }
  }, "The most recent sweep finished ", formatWhen(latest.finished_at), ", more than ", STALE_AFTER_HOURS, " hours ago. These numbers are not current.") : null, latest.scratchpad_refreshed === false ? /*#__PURE__*/React.createElement(Alert, {
    severity: "warning",
    sx: {
      marginBottom: '16px'
    }
  }, "The last sweep did not refresh its ledger. Bills may be landing while the OCR ingest sees nothing new to send.") : null, actionable.length ? /*#__PURE__*/React.createElement(Alert, {
    severity: "warning",
    sx: {
      marginBottom: '16px'
    }
  }, actionable.length === 1 ? '1 provider needs a person' : "".concat(actionable.length, " providers need a person"), ": a bad login, an MFA wall, or config we never set on our side. Everything else is retrying on its own.") : null, /*#__PURE__*/React.createElement(FleetSummary, {
    runs: state.rows
  }), /*#__PURE__*/React.createElement(Grid, {
    container: true,
    spacing: 2,
    sx: {
      marginBottom: '16px'
    }
  }, [{
    label: 'Providers OK',
    value: "".concat((_latest$totals$ok = (_latest$totals = latest.totals) === null || _latest$totals === void 0 ? void 0 : _latest$totals.ok) !== null && _latest$totals$ok !== void 0 ? _latest$totals$ok : 0, " / ").concat((_latest$totals$provid = (_latest$totals2 = latest.totals) === null || _latest$totals2 === void 0 ? void 0 : _latest$totals2.providers) !== null && _latest$totals$provid !== void 0 ? _latest$totals$provid : 0)
  }, {
    label: 'PDFs this run',
    value: (_latest$totals$pdfs = (_latest$totals3 = latest.totals) === null || _latest$totals3 === void 0 ? void 0 : _latest$totals3.pdfs) !== null && _latest$totals$pdfs !== void 0 ? _latest$totals$pdfs : 0
  }, {
    label: 'New to S3',
    value: (_latest$totals$s3_upl = (_latest$totals4 = latest.totals) === null || _latest$totals4 === void 0 ? void 0 : _latest$totals4.s3_uploaded) !== null && _latest$totals$s3_upl !== void 0 ? _latest$totals$s3_upl : 0
  }, {
    label: 'Needs a person',
    value: actionable.length
  }].map(tile => /*#__PURE__*/React.createElement(Grid, {
    item: true,
    xs: 6,
    md: 3,
    key: tile.label
  }, /*#__PURE__*/React.createElement(Paper, {
    sx: {
      padding: '16px'
    }
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: "caption"
  }, tile.label), /*#__PURE__*/React.createElement(Typography, {
    variant: "h5"
  }, tile.value))))), /*#__PURE__*/React.createElement(Funnel, {
    ingest: ingest
  }), /*#__PURE__*/React.createElement(Paper, {
    sx: {
      padding: '16px',
      marginBottom: '16px'
    }
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: "h6",
    gutterBottom: true
  }, "Bills scraped by date"), /*#__PURE__*/React.createElement(Typography, {
    variant: "body2",
    sx: {
      marginBottom: '8px'
    },
    color: "text.secondary"
  }, "Bars are that run's work. Lines are the ledger's running totals, so they are the progress trend: a flat mapped line means the sweep is fetching bills nobody can use yet."), /*#__PURE__*/React.createElement(Box, {
    sx: {
      width: '100%',
      height: 260
    }
  }, /*#__PURE__*/React.createElement(ResponsiveContainer, null, /*#__PURE__*/React.createElement(ComposedChart, {
    data: series
  }, /*#__PURE__*/React.createElement(CartesianGrid, {
    strokeDasharray: "3 3"
  }), /*#__PURE__*/React.createElement(XAxis, {
    dataKey: "date"
  }), /*#__PURE__*/React.createElement(YAxis, {
    yAxisId: "left"
  }), /*#__PURE__*/React.createElement(YAxis, {
    yAxisId: "right",
    orientation: "right"
  }), /*#__PURE__*/React.createElement(Tooltip, null), /*#__PURE__*/React.createElement(Legend, null), /*#__PURE__*/React.createElement(Bar, {
    yAxisId: "left",
    dataKey: "downloaded",
    name: "Downloaded",
    fill: "#8884d8",
    isAnimationActive: false
  }), /*#__PURE__*/React.createElement(Bar, {
    yAxisId: "left",
    dataKey: "uploaded",
    name: "New to S3",
    fill: "#82ca9d",
    isAnimationActive: false
  }), /*#__PURE__*/React.createElement(Line, {
    yAxisId: "right",
    type: "monotone",
    dataKey: "mapped",
    name: "Mapped (total)",
    stroke: "#ed6c02",
    dot: false
  }), /*#__PURE__*/React.createElement(Line, {
    yAxisId: "right",
    type: "monotone",
    dataKey: "posted",
    name: "Sent (total)",
    stroke: "#2e7d32",
    dot: false
  }))))), /*#__PURE__*/React.createElement(Paper, {
    sx: {
      padding: '16px'
    }
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: "h6",
    gutterBottom: true
  }, "Health by provider"), /*#__PURE__*/React.createElement(Table, {
    size: "small"
  }, /*#__PURE__*/React.createElement(TableHead, null, /*#__PURE__*/React.createElement(TableRow, null, /*#__PURE__*/React.createElement(TableCell, null, "Provider"), /*#__PURE__*/React.createElement(TableCell, null, "Status"), /*#__PURE__*/React.createElement(TableCell, {
    align: "right"
  }, "Downloaded"), /*#__PURE__*/React.createElement(TableCell, {
    align: "right"
  }, "New to S3"), /*#__PURE__*/React.createElement(TableCell, {
    align: "right"
  }, "Logins"), /*#__PURE__*/React.createElement(TableCell, {
    align: "right"
  }, "Not due"), /*#__PURE__*/React.createElement(TableCell, null, "Problem"))), /*#__PURE__*/React.createElement(TableBody, null, providers.map(p => {
    var _p$downloaded, _p$s3_uploaded, _p$logins, _p$skipped_not_due;
    return /*#__PURE__*/React.createElement(TableRow, {
      key: p.provider
    }, /*#__PURE__*/React.createElement(TableCell, null, p.provider), /*#__PURE__*/React.createElement(TableCell, null, /*#__PURE__*/React.createElement(Chip, {
      size: "small",
      label: p.status,
      color: p.action_required ? 'error' : isProviderHealthy(p) ? 'success' : 'warning'
    })), /*#__PURE__*/React.createElement(TableCell, {
      align: "right"
    }, (_p$downloaded = p.downloaded) !== null && _p$downloaded !== void 0 ? _p$downloaded : 0), /*#__PURE__*/React.createElement(TableCell, {
      align: "right"
    }, (_p$s3_uploaded = p.s3_uploaded) !== null && _p$s3_uploaded !== void 0 ? _p$s3_uploaded : 0), /*#__PURE__*/React.createElement(TableCell, {
      align: "right"
    }, (_p$logins = p.logins) !== null && _p$logins !== void 0 ? _p$logins : 0), /*#__PURE__*/React.createElement(TableCell, {
      align: "right"
    }, (_p$skipped_not_due = p.skipped_not_due) !== null && _p$skipped_not_due !== void 0 ? _p$skipped_not_due : 0), /*#__PURE__*/React.createElement(TableCell, null, p.failure_class && p.failure_class !== 'none' ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Typography, {
      variant: "body2"
    }, FAILURE_CLASS_LABELS[p.failure_class] || p.failure_class), (p.errors || []).slice(0, 1).map(e => /*#__PURE__*/React.createElement(Typography, {
      variant: "caption",
      key: e
    }, e))) : null));
  })))), /*#__PURE__*/React.createElement(Typography, {
    variant: "caption",
    sx: {
      display: 'block',
      marginTop: '12px'
    }
  }, "Last sweep finished ", formatWhen(latest.finished_at), " in ", latest.mode, " mode."));
});
export const BillScraping = observer(() => {
  const _useState = useState('overview'),
    _useState2 = _slicedToArray(_useState, 2),
    tab = _useState2[0],
    setTab = _useState2[1];
  return /*#__PURE__*/React.createElement(Box, null, /*#__PURE__*/React.createElement(WipBanner, null), /*#__PURE__*/React.createElement(Tabs, {
    "aria-label": "bill scraping tabs",
    onChange: (_, value) => value && setTab(value),
    value: tab
  }, /*#__PURE__*/React.createElement(Tab, {
    label: "Overview",
    value: "overview"
  }), /*#__PURE__*/React.createElement(Tab, {
    label: "Providers",
    value: "providers"
  }), /*#__PURE__*/React.createElement(Tab, {
    label: "Manual vs automated",
    value: "coverage"
  })), tab === 'providers' ? /*#__PURE__*/React.createElement(ProviderCoverage, null) : null, tab === 'coverage' ? /*#__PURE__*/React.createElement(CoverageByGrain, null) : null, tab === 'overview' ? /*#__PURE__*/React.createElement(ScraperOverview, null) : null);
});